import type { ComputedRef, Ref } from "vue";
import type {
  RechercheFacets,
  RechercheFiltresServeur,
} from "~/services/api/rechercheApi";
import type { RechercheCityOption } from "~/composables/useRechercheSearch";
import {
  RECHERCHE_THEMES,
  RECHERCHE_TYPES_ACTIVITE,
  themeCanonique,
  choixTraduits,
} from "~/composables/useRechercheHelpers";

// Type importé de la SOURCE UNIQUE (`#shared/utils/listingTypes`) plutôt que
// redéclaré : cinq copies de cette union vivaient ici, et l'ajout du rayon
// « boutiques » (2026-08-17) les aurait fait diverger une à une.
import type { ValidType } from "#shared/utils/listingTypes";
import { parseDbInstant } from "@donoor/utils/dates";

/**
 * Presets de date dynamiques pour les événements (Karim 2026-06-25).
 *
 * Liste de plages temporelles pré-faites (Aujourd'hui, Demain, Ce week-end,
 * Cette semaine, La semaine prochaine, Ce mois-ci, Le mois prochain).
 * Plus intuitif que 2 date pickers HTML5.
 *
 * On n'affiche dans le modal QUE les presets qui contiennent au moins
 * 1 événement à venir (Karim a précisé : « si on a rien dans la tranche
 * on ne la propose pas »).
 */
export type RechercheDatePresetKey =
  | "today"
  | "tomorrow"
  | "weekend"
  | "this_week"
  | "next_week"
  | "this_month"
  | "next_month";

export interface RechercheDatePreset {
  key: RechercheDatePresetKey;
  label: string;
  /** Calcule [from, to] (timestamps ms) à partir de "maintenant".
   *  Pas une vraie pure-function (utilise Date.now), donc on l'appelle
   *  uniquement dans des computed (réactif). */
  range: () => [number, number];
}

const DATE_PRESETS: ReadonlyArray<RechercheDatePreset> = [
  {
    key: "today",
    label: "Aujourd'hui",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const end = start + 24 * 60 * 60 * 1000 - 1;
      return [start, end];
    },
  },
  {
    key: "tomorrow",
    label: "Demain",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
      const end = start + 24 * 60 * 60 * 1000 - 1;
      return [start, end];
    },
  },
  {
    key: "weekend",
    label: "Ce week-end",
    range: () => {
      const d = new Date();
      const day = d.getDay(); // 0 = dimanche, 6 = samedi
      // Days jusqu'à samedi (si on est déjà samedi → 0, dimanche → -1 → 0)
      const daysUntilSat = day === 0 ? 0 : 6 - day;
      const sat = new Date(d.getFullYear(), d.getMonth(), d.getDate() + daysUntilSat);
      const start = sat.getTime();
      // Sat 00:00 → Dim 23:59:59
      const end = start + 2 * 24 * 60 * 60 * 1000 - 1;
      return [start, end];
    },
  },
  {
    key: "this_week",
    label: "Cette semaine",
    range: () => {
      const d = new Date();
      const day = d.getDay() || 7; // dimanche = 7
      const monday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (day - 1));
      const start = monday.getTime();
      const end = start + 7 * 24 * 60 * 60 * 1000 - 1;
      return [start, end];
    },
  },
  {
    key: "next_week",
    label: "La semaine prochaine",
    range: () => {
      const d = new Date();
      const day = d.getDay() || 7;
      const nextMonday = new Date(d.getFullYear(), d.getMonth(), d.getDate() - (day - 1) + 7);
      const start = nextMonday.getTime();
      const end = start + 7 * 24 * 60 * 60 * 1000 - 1;
      return [start, end];
    },
  },
  {
    key: "this_month",
    label: "Ce mois-ci",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime() - 1;
      return [start, end];
    },
  },
  {
    key: "next_month",
    label: "Le mois prochain",
    range: () => {
      const d = new Date();
      const start = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
      const end = new Date(d.getFullYear(), d.getMonth() + 2, 1).getTime() - 1;
      return [start, end];
    },
  },
];

/**
 * Tranches de prix dynamiques pour les adhésions (Karim 2026-06-25).
 *
 * Au lieu de demander à l'utilisateur de saisir un min/max au clavier
 * (peu intuitif), on lui propose des tranches préfaites : Gratuit,
 * < 10 €, < 50 €, < 100 €, < 500 €. Mais on ne montre QUE les tranches
 * qui contiennent réellement au moins une adhésion → si rien n'est entre
 * 100 € et 500 €, la tranche « < 500 € » disparaît.
 *
 * Format renvoyé : tableau d'objets { label, min, max } prêts à binder.
 * `min`/`max` sont les bornes effectives (max=null = pas de borne haute).
 */
export interface RecherchePriceBucket {
  key: string;
  label: string;
  min: number | null;
  max: number | null;
}

const PRICE_BUCKETS_TEMPLATE: ReadonlyArray<RecherchePriceBucket> = [
  { key: "free", label: "Gratuit", min: 0, max: 0 },
  { key: "lt10", label: "Moins de 10 €", min: 0.01, max: 10 },
  { key: "lt50", label: "Moins de 50 €", min: 10.01, max: 50 },
  { key: "lt100", label: "Moins de 100 €", min: 50.01, max: 100 },
  { key: "lt500", label: "Moins de 500 €", min: 100.01, max: 500 },
  { key: "gt500", label: "Plus de 500 €", min: 500.01, max: null },
];

// Quand on sélectionne un preset, on met à jour dateFrom/dateTo (YYYY-MM-DD).
const formatDateForRange = (ms: number): string => {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export interface UseRechercheFiltersDeps {
  activeType: ComputedRef<ValidType> | Ref<ValidType>;
  /**
   * Contenu de TOUT le rayon actif, renvoyé par l'API (2026-09-26) : les
   * catégories, tranches de prix et raccourcis de dates proposés en
   * découlent. Avant, ils étaient déduits des 30 éléments chargés — une
   * catégorie absente de la première page n'était jamais proposée.
   * `null` tant que l'API n'a pas répondu : on propose alors tout.
   */
  facets: Ref<RechercheFacets | null> | ComputedRef<RechercheFacets | null>;
}

/**
 * useRechercheFilters — état des filtres de la page /recherche (Karim 2026-06-25),
 * extrait 1:1 de `pages/recherche/[[type]].vue` (Zone A split >800 lignes).
 *
 * Le bouton "Filtres" sur la ligne des tabs ouvre un modal centré qui
 * regroupe : ville, catégorie d'activité, tranche de prix (adhésions),
 * date (événements).
 *
 * PERSISTANCE URL (Karim 2026-06-25) — refonte : TOUS les filtres
 * (catégorie, ville, tranche prix, preset date) sont synchronisés avec
 * la query string de l'URL :
 *   /evenements?category=sport&ville=toulouse&price=lt50&date=weekend
 * Ainsi :
 *  - on peut partager le lien (HelloAsso le fait : `?activity_type=…&place_city=…`)
 *  - les filtres survivent à un changement d'onglet (Cagnottes ↔ Asso)
 *    s'ils sont applicables au nouveau type, sinon ignorés silencieusement
 *  - les filtres survivent à un reload de la page
 *
 * NOTE (Karim 2026-06-25) : on NE reset PLUS les filtres au changement
 * d'onglet — au contraire, on les conserve via l'URL. Si tu cliques
 * « Sport » + « Toulouse » sur Cagnottes puis passes sur Asso, les
 * mêmes filtres restent appliqués (HelloAsso fait pareil). Les filtres
 * inapplicables (ex : tranche prix sur Asso) sont juste ignorés
 * silencieusement par les computed `filtered…`.
 */
export async function useRechercheFilters(deps: UseRechercheFiltersDeps) {
  const { activeType, facets } = deps;
  const route = useRoute();
  const router = useRouter();
  const searchQuery = useRechercheSearchQuery();

  // ─── Changement d'onglet = filtres remis à zéro (Karim 2026-09-26) ───
  // Chaque onglet est une page à part : thèmes, ville, prix et dates
  // repartent de SA propre URL (celle des onglets n'en porte aucun). Seul le
  // texte de la barre, état partagé, survivait : « Sport » choisi sur les
  // associations devenait, sur les cagnottes, une recherche du mot « Sport »
  // dans les noms → liste vide. On l'efface quand le RAYON change (pas en
  // changeant de page dans le même rayon), sauf lien entrant `?q=`. Le
  // panneau de recherche, lui, repose son libellé APRÈS la navigation.
  const dernierRayon = useState<string | null>("recherche-dernier-rayon", () => null);
  if (dernierRayon.value && dernierRayon.value !== activeType.value && !route.query.q) {
    searchQuery.value = "";
  }
  dernierRayon.value = activeType.value;

  // Thèmes : LA MÊME LISTE pour associations, cagnottes, événements,
  // adhésions et boutiques (2026-09-26, @donoor/utils themes.ts).
  const RAYONS_A_THEMES = ["associations", "cagnottes", "evenements", "adhesions", "boutiques"];
  const activeCategories = computed(() =>
    RAYONS_A_THEMES.includes(activeType.value) ? choixTraduits(RECHERCHE_THEMES, "theme") : [],
  );

  /** Types d'activité : rayon Événements seulement. */
  const activeActivityTypes = computed(() =>
    activeType.value === "evenements" ? choixTraduits(RECHERCHE_TYPES_ACTIVITE, "type") : [],
  );
  const availableActivityTypes = computed(() => {
    const all = activeActivityTypes.value;
    const presents = facets.value?.types;
    if (all.length === 0 || !presents) return all;
    const present = new Set(presents);
    return all.filter((t) => t.value === "all" || present.has(t.value));
  });

  /**
   * Catégories réellement présentes dans la data du type actif (Karim 2026-06-25).
   *
   * Les constantes RECHERCHE_*_CATEGORIES listent TOUTES les catégories
   * théoriquement possibles (Sport, Culturel, Religieux, etc.) — mais on ne
   * veut afficher dans le modal que celles qui ont AU MOINS 1 entité. Sinon
   * l'utilisateur clique sur « Religieux » et obtient une liste vide.
   *
   * On garde toujours « Toutes » en tête (value === "all").
   */
  const availableCategories = computed(() => {
    const all = activeCategories.value;
    const presentes = facets.value?.categories;
    if (all.length === 0 || !presentes) return all;
    // TOUS les thèmes, avec leur nombre (0 = aucun résultat dans ce rayon) :
    // n'afficher que les thèmes présents laissait 2 vignettes sur les
    // cagnottes (Karim 2026-09-26). La fenêtre grise ceux à 0.
    const comptes = facets.value?.comptes ?? {};
    return all.map((c) => ({ ...c, count: comptes[c.value] ?? 0 }));
  });

  const showFiltersModal = ref(false);

  // Init depuis l'URL (lecture au mount — pas un computed pour qu'on puisse
  // le ré-écrire derrière sans boucle réactive).
  const q = route.query as Record<string, string | string[] | undefined>;
  // Catégorie lue comme THÈME : une ancienne valeur d'un lien partagé
  // (`?category=cultuelle`) devient `spiritualite` — sinon le filtre du
  // navigateur la comparerait aux thèmes en base et masquerait tout.
  //
  // Choix MULTIPLE (2026-09-26) : `?category=sport,loisirs` — un résultat est
  // retenu s'il a L'UN des thèmes (même règle côté API, `themeRetenu`).
  const valeursUrl = (v: unknown): string[] =>
    (Array.isArray(v) ? v : [v])
      .filter((x): x is string => typeof x === "string")
      .flatMap((x) => x.split(","))
      .map((x) => x.trim())
      .filter(Boolean);
  const sansDoublon = (l: string[]) => [...new Set(l)];
  const lireThemes = (v: unknown) =>
    sansDoublon(
      valeursUrl(v)
        .map((x) => themeCanonique(x))
        .filter((x): x is string => !!x && x !== "all"),
    );
  const lireTypes = (v: unknown) =>
    sansDoublon(
      valeursUrl(v).filter(
        (t) => t !== "all" && RECHERCHE_TYPES_ACTIVITE.some((x) => x.value === t),
      ),
    );
  const memeListe = (a: string[], b: string[]) => a.join(",") === b.join(",");
  const initialUrl = {
    categories: lireThemes(q.category),
    types: lireTypes(q.type),
    ville: (Array.isArray(q.ville) ? q.ville[0] : q.ville) || null,
    price: (Array.isArray(q.price) ? q.price[0] : q.price) || null,
    date: (Array.isArray(q.date) ? q.date[0] : q.date) || null,
  };

  /** Thèmes choisis (vide = tous). */
  const selectedCategories = ref<string[]>(initialUrl.categories);
  /** Types d'activité choisis — événements (vide = tous). */
  const selectedActivityTypes = ref<string[]>(initialUrl.types);
  const selectedVille = ref<string | null>(initialUrl.ville);

  // effectiveSearchQuery : version filtrée de searchQuery utilisée par les
  // computed `filtered…` (Karim 2026-06-25). Quand on clique sur une idée
  // d'activité ou une catégorie d'asso dans le panel header, on remplit
  // le champ Quoi avec le label (« Conférences »…) ET on pose ?category=X
  // dans l'URL. Si on appliquait aussi le filter texte sur ce label, on
  // chercherait « Conférences » dans les titres des events → liste vide.
  // Solution : le texte est ignoré quand il n'est QUE le libellé d'un thème
  // ou d'un type choisi (la catégorie fait déjà tout le job). Un texte tapé
  // en plus (« mosquée » avec le thème Spiritualité) cherche DANS le thème —
  // avant, tout texte était ignoré dès qu'un thème était actif (2026-09-26).
  const libellesChoisis = computed(() => {
    const libelles = new Set<string>();
    for (const t of RECHERCHE_THEMES) {
      if (selectedCategories.value.includes(t.value)) {
        libelles.add(t.label.toLowerCase());
        libelles.add(themeAffiche(t.value).toLowerCase());
      }
    }
    for (const t of RECHERCHE_TYPES_ACTIVITE) {
      if (selectedActivityTypes.value.includes(t.value)) {
        libelles.add(t.label.toLowerCase());
        libelles.add(typeActiviteAffiche(t.value).toLowerCase());
      }
    }
    return libelles;
  });
  const effectiveSearchQuery = computed(() =>
    libellesChoisis.value.has(searchQuery.value.trim().toLowerCase()) ? "" : searchQuery.value,
  );

  // priceMin/priceMax sont dérivés du bucket sélectionné via le watch plus
  // bas (cohérent avec le reste du code). On garde 2 refs pour la logique
  // de filter mais l'URL ne stocke que la clé du bucket (plus court).
  const priceMin = ref<number | null>(null);
  const priceMax = ref<number | null>(null);
  const dateFrom = ref<string | null>(null);
  const dateTo = ref<string | null>(null);

  const resetAllFilters = () => {
    selectedCategories.value = [];
    selectedActivityTypes.value = [];
    selectedVille.value = null;
    selectedPriceBucket.value = null;
    selectedDatePreset.value = null;
    priceMin.value = null;
    priceMax.value = null;
    dateFrom.value = null;
    dateTo.value = null;
  };

  // Reset complet utilisé par le bouton « Réinitialiser les filtres » des
  // empty states : inclut aussi searchQuery (la query Quoi du header) pour
  // vraiment revenir à l'état vierge. Karim 2026-06-25.
  const resetAllFiltersAndQuery = () => {
    resetAllFilters();
    searchQuery.value = "";
  };

  // Badge sur le bouton Filtres (nombre de filtres actifs).
  const activeFilterCount = computed(() => {
    let n = 0;
    if (selectedVille.value) n++;
    n += selectedCategories.value.length;
    n += selectedActivityTypes.value.length;
    if (priceMin.value != null || priceMax.value != null) n++;
    if (dateFrom.value || dateTo.value) n++;
    return n;
  });

  const availableDatePresets = computed(() => {
    if (activeType.value !== "evenements") return [];
    const debuts = facets.value?.debuts;
    if (!debuts) return [...DATE_PRESETS];
    const starts: number[] = [];
    for (const d of debuts) {
      // Chaîne naïve de la base = UTC (sinon décalage selon l'écran).
      const t = parseDbInstant(d).getTime();
      if (!Number.isNaN(t)) starts.push(t);
    }
    if (starts.length === 0) return [];
    return DATE_PRESETS.filter((p) => {
      const [from, to] = p.range();
      return starts.some((t) => t >= from && t <= to);
    });
  });

  // Init depuis ?date=... dans l'URL (validation : doit être une clé valide).
  const isValidDatePresetKey = (k: string): k is RechercheDatePresetKey =>
    DATE_PRESETS.some((p) => p.key === k);
  const selectedDatePreset = ref<RechercheDatePresetKey | null>(
    initialUrl.date && isValidDatePresetKey(initialUrl.date)
      ? initialUrl.date
      : null,
  );

  watch(
    selectedDatePreset,
    (key) => {
      if (!key) {
        dateFrom.value = null;
        dateTo.value = null;
        return;
      }
      const p = DATE_PRESETS.find((x) => x.key === key);
      if (!p) return;
      const [from, to] = p.range();
      dateFrom.value = formatDateForRange(from);
      dateTo.value = formatDateForRange(to);
    },
    { immediate: true },
  );

  const availablePriceBuckets = computed<RecherchePriceBucket[]>(() => {
    if (activeType.value !== "adhesions") return [];
    // Prix « à partir de » de TOUTES les adhésions du rayon (API).
    const prices = facets.value?.prix;
    if (!prices) return [...PRICE_BUCKETS_TEMPLATE];
    if (prices.length === 0) return [];
    // Pour chaque tranche, on vérifie qu'au moins 1 prix tombe dedans.
    return PRICE_BUCKETS_TEMPLATE.filter((b) => {
      return prices.some((n) => {
        if (b.min != null && n < b.min) return false;
        if (b.max != null && n > b.max) return false;
        return true;
      });
    });
  });

  // Clé de la tranche actuellement sélectionnée (sinon null = pas de filtre).
  // Init depuis ?price=... dans l'URL (validation : doit exister dans le template).
  const selectedPriceBucket = ref<string | null>(
    initialUrl.price && PRICE_BUCKETS_TEMPLATE.some((b) => b.key === initialUrl.price)
      ? initialUrl.price
      : null,
  );

  // Quand on sélectionne une tranche, on met à jour priceMin/Max effectifs.
  // Synchrone aussi à l'init si une valeur a été lue de l'URL.
  watch(
    selectedPriceBucket,
    (key) => {
      if (!key) {
        priceMin.value = null;
        priceMax.value = null;
        return;
      }
      const b = PRICE_BUCKETS_TEMPLATE.find((x) => x.key === key);
      if (!b) return;
      priceMin.value = b.min;
      priceMax.value = b.max;
    },
    { immediate: true },
  );

  // ─── SYNC URL ↔ filtres (Karim 2026-06-25) ──────────────────────────
  // Un seul watch qui pousse tous les filtres dans la query string :
  //   ?category=sport&ville=toulouse&price=lt50&date=weekend
  // Permet le partage de lien (HelloAsso : `?activity_type=…&place_city=…`)
  // et survit aux reload/changement d'onglet. router.replace = pas d'entrée
  // historique pour chaque toggle de pill (sinon back button = enfer).
  watch(
    () =>
      [
        selectedCategories.value.join(","),
        selectedVille.value,
        selectedPriceBucket.value,
        selectedDatePreset.value,
        selectedActivityTypes.value.join(","),
      ] as const,
    ([cat, ville, price, date, type]) => {
      const query: Record<string, string> = {};
      if (cat) query.category = cat;
      if (type) query.type = type;
      if (ville) query.ville = ville;
      if (price) query.price = price;
      if (date) query.date = date;
      router.replace({ query });
    },
  );

  // ─── SYNC URL → filtres (2026-09-26) ─────────────────────────────────
  // L'URL n'était lue qu'au montage. Or le panneau de recherche de l'en-tête
  // change la query SANS recharger la page (clic sur « Sport » →
  // ?category=sportive) : l'état restait sur « Toutes », la recherche
  // effective gardait le texte « Sport », et l'API cherchait « Sport » dans
  // les NOMS d'associations au lieu de filtrer la catégorie. Toute query
  // arrivant de l'extérieur réaligne donc l'état ; nos propres écritures
  // (watch ci-dessus) retombent sur des valeurs identiques, sans boucle.
  watch(
    () => route.query,
    (rq) => {
      const lire = (v: unknown) => (Array.isArray(v) ? v[0] : v) as string | undefined;
      const cats = lireThemes(rq.category);
      const types = lireTypes(rq.type);
      if (!memeListe(selectedActivityTypes.value, types)) selectedActivityTypes.value = types;
      const ville = lire(rq.ville) || null;
      const price = lire(rq.price) || null;
      const date = lire(rq.date) || null;
      if (!memeListe(selectedCategories.value, cats)) selectedCategories.value = cats;
      if (selectedVille.value !== ville) selectedVille.value = ville;
      const prixValide =
        price && PRICE_BUCKETS_TEMPLATE.some((b) => b.key === price) ? price : null;
      if (selectedPriceBucket.value !== prixValide) selectedPriceBucket.value = prixValide;
      const dateValide = date && isValidDatePresetKey(date) ? date : null;
      if (selectedDatePreset.value !== dateValide) selectedDatePreset.value = dateValide;
    },
  );

  // ─── Villes dynamiques (Karim 2026-06-15 — SEO sprint v4) ────────────
  // Fetch la liste des villes ayant ≥1 entité du type actif. Affichées dans
  // le dropdown filtres ; click → navigation vers la page programmatique
  // /<type>/[ville] (cohérent avec /associations/ville/[ville]).
  const citiesEndpoint = computed(() => {
    if (activeType.value === "cagnottes") return "/api/campaigns/by-city";
    if (activeType.value === "evenements") return "/api/events/by-city";
    if (activeType.value === "adhesions") return "/api/memberships/by-city";
    if (activeType.value === "associations") return "/api/organizations/by-city";
    return null;
  });
  // ⚠️ Pas de `await` ici : les watchers ci-dessous doivent être enregistrés
  // de façon SYNCHRONE (avant tout await) pour rester rattachés au scope du
  // composant (sinon fuite de watchers après navigation). L'`await` SSR est
  // fait tout en bas du composable.
  const citiesAsync = useFetch<{ cities?: RechercheCityOption[] }>(
    () => citiesEndpoint.value || "/api/campaigns/by-city",
    {
      key: `cities-${activeType.value}`,
      server: true,
      lazy: false,
      default: () => ({ cities: [] }),
      watch: [activeType],
    },
  );
  const { data: citiesData } = citiesAsync;
  const availableCities = computed<RechercheCityOption[]>(
    () => citiesData.value?.cities || [],
  );

  // ─── Sync des composables partagés vers AppHeader (Karim 2026-06-25) ──
  // La searchbar enrichie (RechercheGlobalSearch) vit dans le header
  // global et a besoin des cities + selectedVille + résultats live.
  // On pousse ces refs dans useState pour que le header les lise.
  const sharedCities = useRechercheCities();
  const sharedSelectedVille = useRechercheSelectedVille();

  watch(availableCities, (cs) => {
    sharedCities.value = cs;
  });

  // 2-way binding selectedVille : la page écoute les changements depuis
  // le header (clic sur une ville dans le panel) et le header écoute les
  // changements depuis la page (init URL, clic sur filtre modal).
  watch(
    selectedVille,
    (v) => {
      if (sharedSelectedVille.value !== v) sharedSelectedVille.value = v;
    },
    { immediate: true },
  );
  watch(sharedSelectedVille, (v) => {
    if (selectedVille.value !== v) selectedVille.value = v;
  });

  // Await SSR du fetch cities (placé en fin de composable — cf. note plus
  // haut) puis application "immediate" manuelle vers le state partagé,
  // exactement comme le faisait le `watch(..., { immediate: true })`
  // positionné après l'`await` dans l'ancien code inline de la page.
  await citiesAsync;
  sharedCities.value = availableCities.value;

  /**
   * Filtres transmis à l'API (appliqués AVANT pagination, sur tout le
   * rayon). Seuls ceux qui ont un sens pour le rayon actif partent : un
   * filtre inapplicable (prix sur les associations…) reste ignoré, comme
   * avant.
   */
  const filtresServeur = computed<RechercheFiltresServeur>(() => {
    const t = activeType.value;
    const f: RechercheFiltresServeur = {};
    if (t === "tout" || t === "qr-codes") return f;
    if (selectedCategories.value.length) f.category = selectedCategories.value.join(",");
    if (selectedVille.value) f.ville = selectedVille.value;
    if (t === "evenements") {
      if (selectedActivityTypes.value.length) f.type = selectedActivityTypes.value.join(",");
      if (dateFrom.value) f.date_from = dateFrom.value;
      if (dateTo.value) f.date_to = dateTo.value;
    }
    if (t === "adhesions") {
      if (priceMin.value != null) f.price_min = priceMin.value;
      if (priceMax.value != null) f.price_max = priceMax.value;
    }
    return f;
  });

  return {
    showFiltersModal,
    filtresServeur,
    selectedActivityTypes,
    availableActivityTypes,
    selectedCategories,
    selectedVille,
    selectedPriceBucket,
    selectedDatePreset,
    priceMin,
    priceMax,
    dateFrom,
    dateTo,
    effectiveSearchQuery,
    resetAllFilters,
    resetAllFiltersAndQuery,
    activeFilterCount,
    availableCategories,
    availableDatePresets,
    availablePriceBuckets,
    availableCities,
  };
}
